/**
 * Medication Calculation Utilities
 *
 * Shared utilities for sulfadimethoxine dosage calculations across all species.
 * All calculations based on current veterinary pharmacology standards.
 */

export interface DosageResult {
  totalDoseMg: number;
  volumeMl?: number;
  tabletDose?: string;
  weightKg: number;
  dosePerKg: number;
}

export interface WeightConversion {
  kg: number;
  lbs: number;
}

export interface DosageValidation {
  valid: boolean;
  message: string;
  severity: 'info' | 'warning' | 'error';
}

/**
 * Species-specific dosage protocols
 * Based on veterinary pharmacology references
 */
export const DOSAGE_PROTOCOLS = {
  dog: {
    loading: 55, // mg/kg on day 1
    maintenance: 27.5, // mg/kg days 2+
    minWeight: 1, // kg
    maxWeight: 100, // kg
    duration: '5-7 days',
    name: 'Dog (Canine)'
  },
  cat: {
    loading: 50, // mg/kg on day 1
    maintenance: 25, // mg/kg days 2+
    minWeight: 0.5, // kg
    maxWeight: 15, // kg
    duration: '5 days maximum',
    name: 'Cat (Feline)'
  },
  cattle: {
    loading: 55, // mg/kg on day 1
    maintenance: 27.5, // mg/kg days 2+
    minWeight: 50, // kg
    maxWeight: 1000, // kg
    duration: '5 days',
    name: 'Cattle (Bovine)',
    withdrawalMeat: '7-10 days',
    withdrawalMilk: 'Not approved for lactating dairy cattle'
  },
  swine: {
    loading: 55, // mg/kg on day 1
    maintenance: 27.5, // mg/kg days 2+
    minWeight: 10, // kg
    maxWeight: 300, // kg
    duration: '3-5 days',
    name: 'Swine (Porcine)',
    withdrawalMeat: '15 days'
  },
  sheep: {
    loading: 50, // mg/kg on day 1
    maintenance: 25, // mg/kg days 2+
    minWeight: 10, // kg
    maxWeight: 150, // kg
    duration: '3-5 days',
    name: 'Sheep/Goat',
    extraLabel: true,
    withdrawalMeat: '10 days minimum (consult veterinarian)'
  },
  poultry: {
    loading: 25, // mg/kg on day 1
    maintenance: 25, // mg/kg days 2+
    minWeight: 0.5, // kg
    maxWeight: 10, // kg
    duration: '3-5 days',
    name: 'Poultry (Avian)',
    waterMedication: '0.05-0.1% solution',
    withdrawalEggs: '3-5 days'
  },
  rabbit: {
    loading: 50, // mg/kg on day 1
    maintenance: 25, // mg/kg days 2+
    minWeight: 0.5, // kg
    maxWeight: 7, // kg
    duration: '5-10 days',
    name: 'Rabbit (Small Mammal)'
  },
  horse: {
    loading: 20, // mg/kg on day 1 (lower dose for horses)
    maintenance: 15, // mg/kg days 2+
    minWeight: 100, // kg
    maxWeight: 700, // kg
    duration: '3-5 days',
    name: 'Horse (Equine)',
    extraLabel: true,
    note: 'Extra-label use - veterinary supervision required'
  }
} as const;

export type SpeciesType = keyof typeof DOSAGE_PROTOCOLS;

/**
 * Available medication formulations
 */
export const FORMULATIONS = {
  tablet: {
    sizes: [125, 250, 500], // mg
    name: 'Tablet'
  },
  suspension: {
    concentration: 50, // mg/mL (5% solution)
    name: 'Oral Suspension'
  },
  injectable: {
    concentration: 400, // mg/mL (40% solution)
    name: 'Injectable Solution'
  },
  powder: {
    name: 'Oral Powder',
    note: 'Weigh accurately for proper dosing'
  }
} as const;

/**
 * Convert weight between units
 */
export function convertWeight(
  weight: number,
  fromUnit: 'kg' | 'lbs'
): WeightConversion {
  if (fromUnit === 'lbs') {
    const kg = weight * 0.453592;
    return {
      kg: Math.round(kg * 10) / 10,
      lbs: weight
    };
  } else {
    const lbs = weight * 2.20462;
    return {
      kg: weight,
      lbs: Math.round(lbs * 10) / 10
    };
  }
}

/**
 * Calculate dosage for a given weight and species
 */
export function calculateDosage(
  weightKg: number,
  species: SpeciesType,
  doseType: 'loading' | 'maintenance' = 'loading'
): DosageResult {
  const protocol = DOSAGE_PROTOCOLS[species];
  const dosePerKg = doseType === 'loading' ? protocol.loading : protocol.maintenance;
  const totalDoseMg = weightKg * dosePerKg;

  return {
    totalDoseMg: Math.round(totalDoseMg * 10) / 10,
    weightKg,
    dosePerKg
  };
}

/**
 * Calculate tablet dosage (combinations of available sizes)
 */
export function calculateTabletDose(
  doseMg: number,
  tabletSizes: number[] = FORMULATIONS.tablet.sizes
): string {
  const tablets: string[] = [];
  let remainder = doseMg;

  // Sort tablets largest to smallest
  const sortedSizes = [...tabletSizes].sort((a, b) => b - a);

  // Calculate whole tablets
  for (const size of sortedSizes) {
    const count = Math.floor(remainder / size);
    if (count > 0) {
      tablets.push(`${count} × ${size}mg`);
      remainder -= count * size;
    }
  }

  // Handle fractional tablets if significant remainder
  if (remainder > 5) {
    const closestSize = sortedSizes.reduce((prev, curr) =>
      Math.abs(curr - remainder) < Math.abs(prev - remainder) ? curr : prev
    );
    const fraction = roundToNearestFraction(remainder / closestSize);
    if (fraction > 0) {
      tablets.push(`${fractionToString(fraction)} × ${closestSize}mg`);
    }
  }

  return tablets.length > 0 ? tablets.join(' + ') : '0mg';
}

/**
 * Calculate liquid dose volume
 */
export function calculateLiquidDose(
  doseMg: number,
  concentrationMgPerMl: number = FORMULATIONS.suspension.concentration
): number {
  const volumeMl = doseMg / concentrationMgPerMl;
  return Math.round(volumeMl * 100) / 100; // Round to 2 decimal places
}

/**
 * Validate dosage is within safe range for species
 */
export function validateDosageRange(
  weightKg: number,
  species: SpeciesType,
  doseMgPerKg: number
): DosageValidation {
  const protocol = DOSAGE_PROTOCOLS[species];

  // Check weight range
  if (weightKg < protocol.minWeight) {
    return {
      valid: false,
      message: `Weight below minimum for ${protocol.name} (${protocol.minWeight}kg). Please verify.`,
      severity: 'error'
    };
  }

  if (weightKg > protocol.maxWeight) {
    return {
      valid: false,
      message: `Weight above typical maximum for ${protocol.name} (${protocol.maxWeight}kg). Please verify.`,
      severity: 'warning'
    };
  }

  // Check dose range
  const maxExpectedDose = protocol.loading * 1.2; // 20% tolerance
  const minExpectedDose = protocol.maintenance * 0.8; // 20% tolerance

  if (doseMgPerKg > maxExpectedDose) {
    return {
      valid: false,
      message: `Dose above typical range for ${protocol.name}. Veterinary consultation required.`,
      severity: 'error'
    };
  }

  if (doseMgPerKg < minExpectedDose) {
    return {
      valid: true,
      message: `Dose below typical range for ${protocol.name}. May be appropriate for specific cases.`,
      severity: 'info'
    };
  }

  return {
    valid: true,
    message: `Dose within typical range for ${protocol.name}.`,
    severity: 'info'
  };
}

/**
 * Round number to nearest common fraction (0.25, 0.5, 0.75, 1)
 */
function roundToNearestFraction(num: number): number {
  const fractions = [0, 0.25, 0.5, 0.75, 1];
  return fractions.reduce((prev, curr) =>
    Math.abs(curr - num) < Math.abs(prev - num) ? curr : prev
  );
}

/**
 * Convert decimal fraction to readable string
 */
function fractionToString(fraction: number): string {
  if (fraction === 0.25) return '¼';
  if (fraction === 0.5) return '½';
  if (fraction === 0.75) return '¾';
  if (fraction === 1) return '1';
  return fraction.toString();
}

/**
 * Generate dosing schedule text
 */
export function generateDosageSchedule(
  species: SpeciesType,
  weightKg: number,
  formulation: 'tablet' | 'suspension',
  tabletSizes?: number[]
): { day1: string; maintenance: string; duration: string; notes: string[] } {
  const protocol = DOSAGE_PROTOCOLS[species];
  const loadingDose = calculateDosage(weightKg, species, 'loading');
  const maintenanceDose = calculateDosage(weightKg, species, 'maintenance');

  let day1: string;
  let maintenance: string;
  const notes: string[] = [];

  if (formulation === 'tablet') {
    day1 = calculateTabletDose(loadingDose.totalDoseMg, tabletSizes);
    maintenance = calculateTabletDose(maintenanceDose.totalDoseMg, tabletSizes);
  } else {
    const day1Ml = calculateLiquidDose(loadingDose.totalDoseMg);
    const maintenanceMl = calculateLiquidDose(maintenanceDose.totalDoseMg);
    day1 = `${day1Ml} mL`;
    maintenance = `${maintenanceMl} mL`;
  }

  // Add species-specific notes
  if (protocol.extraLabel) {
    notes.push('Extra-label use - requires veterinary supervision');
  }
  if (protocol.withdrawalMeat) {
    notes.push(`Meat withdrawal: ${protocol.withdrawalMeat}`);
  }
  if (protocol.withdrawalMilk) {
    notes.push(`Milk: ${protocol.withdrawalMilk}`);
  }
  if (protocol.withdrawalEggs) {
    notes.push(`Egg withdrawal: ${protocol.withdrawalEggs}`);
  }
  if (protocol.waterMedication) {
    notes.push(`Water medication: ${protocol.waterMedication}`);
  }
  if (protocol.note) {
    notes.push(protocol.note);
  }

  return {
    day1,
    maintenance,
    duration: protocol.duration,
    notes
  };
}

/**
 * Get species emoji for UI display
 */
export function getSpeciesEmoji(species: SpeciesType): string {
  const emojis: Record<SpeciesType, string> = {
    dog: '🐕',
    cat: '🐱',
    cattle: '🐄',
    swine: '🐷',
    sheep: '🐑',
    poultry: '🐔',
    rabbit: '🐰',
    horse: '🐴'
  };
  return emojis[species] || '🐾';
}

/**
 * Get species common name
 */
export function getSpeciesName(species: SpeciesType): string {
  return DOSAGE_PROTOCOLS[species].name;
}
